import React, { useState, useRef, useEffect, useCallback } from "react";
import CreatableSelect from "react-select/creatable";
import { useDispatch } from "react-redux";
import { getFolders, getListsByFolder, createFolder, createList, uploadCandidateResumes } from "../../Redux/ActionCreators/candidateAction";
import { Snackbar, Alert } from "@mui/material";

export const MAX_FILES = 20;
export const ALLOWED_EXTENSIONS = [".pdf", ".docx"];
export const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const readDirectoryFiles = (directoryEntry) => {
    return new Promise((resolve) => {
        const results = [];
        const reader = directoryEntry.createReader();

        const readEntries = () => {
            reader.readEntries(async (entries) => {
                if (!entries.length) {
                    resolve(results);
                    return;
                }
                for (const entry of entries) {
                    if (entry.isFile) {
                        await new Promise((res) => {
                            entry.file((file) => {
                                results.push(file);
                                res();
                            });
                        });
                    } else if (entry.isDirectory) {
                        const nested = await readDirectoryFiles(entry);
                        results.push(...nested);
                    }
                }
                readEntries();
            });
        };

        readEntries();
    });
};

export const filterValidFiles = (files) =>
    files.filter((file) => {
        const ext = file.name
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();
        return (
            ALLOWED_MIME_TYPES.includes(file.type) ||
            ALLOWED_EXTENSIONS.includes(ext)
        );
    });

const FileTypeIcon = ({ name }) => {
    const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
    const isPdf = ext === ".pdf";
    return (
        <div
            style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                background: isPdf ? "#FFF0F4" : "#F0F4FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "10px",
                fontWeight: 700,
                color: isPdf ? "#F75891" : "#4A6EE0",
                border: isPdf ? "1px solid #F7589133" : "1px solid #4A6EE033",
            }}
        >
            {isPdf ? "PDF" : "DOC"}
        </div>
    );
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        boxShadow: state.isFocused ? "0 0 0 1px #111927" : "none",
        borderRadius: "4px",
        fontSize: "14px",
        lineHeight: "18px",
        minHeight: "40px",
        "&:hover": { borderColor: "#1f2a37" },
        color: "#1f2a37",
        fontFamily: '"Inter", sans-serif'
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "14px",
        color: state.isSelected ? "white" : "#111927",
    }),
    placeholder: (base) => ({ ...base, color: "#1f2a37", fontFamily: '"Inter", sans-serif' }),
    option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
};

const StepIndicator = ({ current }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            margin: "20px 0",
        }}
    >
        {[1, 2].map((n) => (
            <React.Fragment key={n}>
                <div
                    style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 700,
                        background: current >= n ? "#F75891" : "#F0F0F0",
                        color: current >= n ? "#fff" : "#8A9199",
                        transition: "background 0.25s",
                    }}
                >
                    {n}
                </div>
                {n < 2 && (
                    <div
                        style={{
                            width: "40px",
                            height: "2px",
                            background: current > 1 ? "#F75891" : "#E4E9F0",
                            borderRadius: "2px",
                            transition: "background 0.25s",
                        }}
                    />
                )}
            </React.Fragment>
        ))}
    </div>
);

const labelStyle = {
    display: "block",
    fontSize: "14px",
    lineHeight: "21px",
    fontWeight: 600,
    color: "#111928",
    marginBottom: "6px",
};

const AddCandidatesModal = ({ show, onClose, onUpload, preselect, onFolderCreated, onListCreated }) => {
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [folderOptions, setFolderOptions] = useState([]);
    const [listOptions, setListOptions] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const [folderLoading, setFolderLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);

    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const dragCounter = useRef(0);

    const preselected = useRef(false);
    useEffect(() => {
        if (!show) {
            setStep(1);
            setSelectedFolder(null);
            setSelectedList(null);
            setFolderOptions([]);
            setListOptions([]);
            setFiles([]);
            setUploadError(null);
            setUploading(false);
            preselected.current = false;
            if (fileInputRef.current) fileInputRef.current.value = null;
            if (addMoreInputRef.current) addMoreInputRef.current.value = null;
        }
    }, [show]);

    useEffect(() => {
        if (!show) return;
        setFolderLoading(true);
        dispatch(getFolders()).then((res) => {
            setFolderLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                const opts = res.data.map((f) => ({ value: f.id, label: f.name }));
                setFolderOptions(opts);
                if (preselect?.folder && !preselected.current) {
                    const match = opts.find((o) => o.value === preselect.folder.value);
                    if (match) setSelectedFolder(match);
                }
            } else {
                setFolderOptions([]);
            }
        });
    }, [show]);

    useEffect(() => {
        if (!selectedFolder) {
            setListOptions([]);
            setSelectedList(null);
            return;
        }
        setListLoading(true);
        setSelectedList(null);
        dispatch(getListsByFolder(selectedFolder.value)).then((res) => {
            setListLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                const opts = res.data.map((l) => ({ value: l.id, label: l.name }));
                setListOptions(opts);
                if (preselect?.list && !preselected.current) {
                    const match = opts.find((o) => o.value === preselect.list.value);
                    if (match) {
                        setSelectedList(match);
                        preselected.current = true;
                    }
                }
            } else {
                setListOptions([]);
            }
        });
    }, [selectedFolder]);

    const addFiles = useCallback((incoming) => {
        const valid = filterValidFiles(incoming);
        setFiles((prev) => {
            const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
            const unique = valid.filter(
                (f) => !existing.has(`${f.name}-${f.size}`)
            );
            return [...prev, ...unique].slice(0, MAX_FILES);
        });
    }, []);

    const handleBrowse = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleAddMore = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current += 1;
        if (dragCounter.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

    const handleDrop = async (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        const items = Array.from(e.dataTransfer.items || []);
        const collected = [];

        for (const item of items) {
            if (item.kind !== "file") continue;
            const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
            if (entry) {
                if (entry.isDirectory) {
                    const nested = await readDirectoryFiles(entry);
                    collected.push(...nested);
                } else {
                    const file = item.getAsFile();
                    if (file) collected.push(file);
                }
            } else {
                const file = item.getAsFile();
                if (file) collected.push(file);
            }
        }

        addFiles(collected);
    };

    const removeFile = (index) =>
        setFiles((prev) => prev.filter((_, i) => i !== index));

    const handleUploadClick = async () => {
        if (!files.length) return;
        if (!selectedFolder || !selectedList) {
            setUploadError("Folder and list must be selected. Please go back and complete Step 1.");
            return;
        }
        setUploading(true);
        setUploadError(null);
        const result = await dispatch(
            uploadCandidateResumes(
                selectedFolder.value,
                selectedList.value,
                files
            )
        );
        setUploading(false);
        if (result?.success) {
            if (onUpload) onUpload(files);
            onClose();
        } else {
            setUploadError(result?.message || "Upload failed. Please try again.");
        }
    };

    const handleCreateFolder = async (inputValue) => {
        const body = {
            name: inputValue,
            is_active: true,
        };
        const result = await dispatch(createFolder(body));
        if (result?.success) {
            const folderId = result.data?.id || result.data;
            const newOpt = { value: folderId, label: inputValue };
            setFolderOptions((prev) => [...prev, newOpt]);
            setSelectedFolder(newOpt);

            setSnackbar({ open: true, message: "Folder created successfully", severity: "success" });
            if (onFolderCreated) onFolderCreated();
        } else {
            setFolderLoading(true);
            dispatch(getFolders()).then((res) => {
                setFolderLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
                } else {
                    setFolderOptions([]);
                }
            });
            const errorMsg = result?.status === 400 ? "Folder name already exists" : (result?.message || "Folder creation failed");
            setSnackbar({ open: true, message: errorMsg, severity: "error" });
        }
    };

    const handleCreateList = async (inputValue) => {
        if (!selectedFolder) return;
        const body = {
            name: inputValue,
            type: "manual",
            is_active: true,
            folder_id: selectedFolder.value,
            rules: [],
        };
        const result = await dispatch(createList(body));
        if (result?.success) {
            const listId = result.data?.id || result.data;
            const newOpt = { value: listId, label: inputValue };
            setListOptions((prev) => [...prev, newOpt]);
            setSelectedList(newOpt);

            setSnackbar({ open: true, message: "List created successfully", severity: "success" });
            if (onListCreated) onListCreated(selectedFolder.value);
        } else {
            setListLoading(true);
            dispatch(getListsByFolder(selectedFolder.value)).then((res) => {
                setListLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setListOptions(res.data.map((l) => ({ value: l.id, label: l.name })));
                } else {
                    setListOptions([]);
                }
            });
            const errorMsg = result?.status === 400 ? "List name already exists" : (result?.message || "List creation failed");
            setSnackbar({ open: true, message: errorMsg, severity: "error" });
        }
    };

    if (!show) return null;

    const hasFiles = files.length > 0;
    const canProceed = selectedFolder && selectedList;

    return (
        <div className="upload-modal">
            <div className="modal-content" style={{ maxWidth: "520px", width: "100%" }}>
                <div className="modal-head">
                    <div />
                    <h3>Add Candidates</h3>
                    <img
                        src="/images/close-icon.svg"
                        alt="close"
                        onClick={onClose}
                        style={{ cursor: "pointer" }}
                    />
                </div>

                <StepIndicator current={step} />

                {step === 1 && (
                    <div style={{ padding: "0 20px 20px" }}>
                        <p
                            style={{
                                fontSize: "14px",
                                color: "#8A9199",
                                marginBottom: "20px",
                                textAlign: "center",
                            }}
                        >
                            Organise candidates by selecting a folder and list.
                        </p>

                        <div style={{ marginBottom: "18px" }}>
                            <label style={labelStyle}>Select Folder</label>
                            <CreatableSelect
                                className="mt-2"
                                isClearable
                                isLoading={folderLoading}
                                isDisabled={folderLoading}
                                options={folderOptions}
                                value={selectedFolder}
                                onChange={setSelectedFolder}
                                onCreateOption={handleCreateFolder}
                                styles={selectStyles}
                                placeholder="Choose or create a folder…"
                                formatCreateLabel={(val) => `+ Create folder "${val}"`}
                            />
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <label style={labelStyle}>Select List</label>
                            <CreatableSelect
                                className="mt-2"
                                isClearable
                                isLoading={listLoading}
                                isDisabled={!selectedFolder || listLoading}
                                options={listOptions}
                                value={selectedList}
                                onChange={setSelectedList}
                                onCreateOption={handleCreateList}
                                styles={selectStyles}
                                placeholder={selectedFolder ? "Choose or create a list…" : "Select a folder first"}
                                formatCreateLabel={(val) => `+ Create list "${val}"`}
                            />
                        </div>

                        <div className="d-flex justify-content-end">
                            <button
                                className="upload-btn"
                                onClick={() => setStep(2)}
                                disabled={!canProceed}
                                style={{
                                    opacity: !canProceed ? 0.5 : 1,
                                    cursor: !canProceed ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <>
                        {!hasFiles && (
                            <div
                                className="modal-input"
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() =>
                                    fileInputRef.current && fileInputRef.current.click()
                                }
                                style={{
                                    border: isDragging
                                        ? "2px dashed #F75891"
                                        : "2px dashed #E4E9F0",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    minHeight: "180px",
                                    justifyContent: "center",
                                    padding: "28px 24px",
                                    transition: "border-color 0.2s, background 0.2s",
                                    background: isDragging ? "#FFF5F9" : "#FAFBFC",
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.docx"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleBrowse}
                                    onClick={(e) => e.stopPropagation()}
                                />

                                <img
                                    src="/images/upload-icon-pink.svg"
                                    alt=""
                                    style={{ marginBottom: "8px" }}
                                />
                                <p style={{ margin: "4px 0" }}>
                                    <strong style={{ fontWeight: 600 }}>Click to upload</strong>{" "}
                                    or drag and drop
                                </p>
                                <p
                                    style={{
                                        color: "#8A9199",
                                        fontSize: "13px",
                                        margin: "2px 0",
                                    }}
                                >
                                    Supported formats:{" "}
                                    <strong style={{ color: "#525B66" }}>.pdf</strong>,{" "}
                                    <strong style={{ color: "#525B66" }}>.docx</strong>
                                </p>
                                <p
                                    style={{
                                        color: "#B0B8C1",
                                        fontSize: "12px",
                                        margin: "2px 0",
                                    }}
                                >
                                    You can also drop a folder — we'll extract PDF &amp; DOCX files
                                    inside it.
                                </p>
                            </div>
                        )}

                        {hasFiles && (
                            <div style={{ marginTop: "4px", padding: "20px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginBottom: "10px",
                                    }}
                                >
                                    <p
                                        style={{
                                            margin: 0,
                                            fontWeight: 600,
                                            fontSize: "14px",
                                            color: "#525B66",
                                        }}
                                    >
                                        {files.length} file{files.length > 1 ? "s" : ""} selected
                                    </p>
                                    <span style={{ fontSize: "12px", color: "#8A9199" }}>
                                        max {MAX_FILES}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        maxHeight: "240px",
                                        overflowY: "auto",
                                        scrollbarWidth: "thin",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "6px",
                                    }}
                                >
                                    {files.map((f, idx) => (
                                        <>
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    padding: "8px 12px",
                                                    borderRadius: "8px",
                                                    background: "#F7F9FC",
                                                    border: "1px solid #E4E9F0",
                                                }}
                                            >
                                                <FileTypeIcon name={f.name} />

                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            fontSize: "13px",
                                                            fontWeight: 500,
                                                            color: "#2D3748",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        title={f.name}
                                                    >
                                                        {f.name}
                                                    </p>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            fontSize: "11px",
                                                            color: "#8A9199",
                                                        }}
                                                    >
                                                        {(f.size / 1024).toFixed(1)} KB
                                                    </p>
                                                </div>

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeFile(idx);
                                                    }}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        color: "#8A9199",
                                                        fontSize: "18px",
                                                        lineHeight: 1,
                                                        flexShrink: 0,
                                                        padding: "0 2px",
                                                        transition: "color 0.15s",
                                                    }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.color = "#FF1C58")
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.color = "#8A9199")
                                                    }
                                                    title="Remove file"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </>
                                    ))}
                                    {files.length < MAX_FILES && (
                                        <div>
                                            <input
                                                ref={addMoreInputRef}
                                                type="file"
                                                accept=".pdf,.docx"
                                                multiple
                                                style={{ display: "none" }}
                                                onChange={handleAddMore}
                                            />
                                            <button
                                                className="upload-btn"
                                                onClick={() =>
                                                    addMoreInputRef.current &&
                                                    addMoreInputRef.current.click()
                                                }
                                                disabled={files.length >= MAX_FILES}
                                                style={{
                                                    border: "1px solid #111928",
                                                    color: "#111928",
                                                    backgroundColor: "transparent",
                                                    cursor:
                                                        files.length >= MAX_FILES
                                                            ? "not-allowed"
                                                            : "pointer",
                                                    opacity: files.length >= MAX_FILES ? 0.5 : 1,
                                                    margin: '0'
                                                }}
                                            >
                                                + Add more candidates
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {uploadError && (
                            <p style={{ color: "#FF1C58", fontSize: "13px", padding: "0 20px", margin: "4px 0" }}>
                                {uploadError}
                            </p>
                        )}

                        <div
                            className="d-flex justify-content-between"
                            style={{ marginTop: "20px", gap: "12px" }}
                        >
                            <button
                                className="upload-btn"
                                onClick={() => setStep(1)}
                                disabled={uploading}
                                style={{
                                    border: "1px solid #111928",
                                    color: "#111928",
                                    backgroundColor: "transparent",
                                    cursor: uploading ? "not-allowed" : "pointer",
                                }}
                            >
                                Back
                            </button>
                            <button
                                className="upload-btn"
                                onClick={handleUploadClick}
                                disabled={files.length === 0 || uploading}
                                style={{ opacity: files.length === 0 || uploading ? 0.5 : 1 }}
                            >
                                {uploading ? "Uploading…" : "Upload candidates"}
                            </button>
                        </div>
                    </>
                )}
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AddCandidatesModal;
